import { useState, useCallback, useEffect } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface ProjectGalleryProps {
  images: string[];
  projectTitle: string;
}

export default function ProjectGallery({ images, projectTitle }: ProjectGalleryProps) {
  const { ref, isVisible } = useScrollReveal();
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  }, []);

  const nextImage = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevImage = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, closeLightbox, nextImage, prevImage]);

  // Touch/swipe for lightbox
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextImage();
      else prevImage();
    }
    setTouchStart(null);
  };

  if (!images || images.length === 0) return null;

  return (
    <>
      <section ref={ref} className="w-full py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16">
          <div className={`flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-14 gap-4 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-3">
                Visual Tour
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-charcoal-300 leading-tight">
                Project <span className="text-[#d4853a]">Gallery</span>
              </h2>
            </div>
            <div className="text-[13px] text-charcoal-50">
              {activeIndex + 1} / {images.length}
            </div>
          </div>

          {/* Main showcase + thumbnails */}
          <div className={`grid grid-cols-1 lg:grid-cols-[1fr_200px] gap-4 md:gap-5 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {/* Main large image */}
            <div className="relative rounded-2xl overflow-hidden cursor-pointer group" onClick={() => openLightbox(activeIndex)}>
              <div className="aspect-[16/10] md:aspect-[16/9]">
                <img
                  src={images[activeIndex]}
                  alt={`${projectTitle} - View ${activeIndex + 1}`}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-500 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 scale-90 group-hover:scale-100">
                  <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl">
                    <i className="ri-fullscreen-line text-xl text-charcoal-300" />
                  </div>
                </div>
              </div>
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 bg-gradient-to-t from-black/60 to-transparent">
                <p className="text-white/90 text-sm font-medium">{projectTitle}</p>
                <p className="text-white/50 text-xs mt-0.5">Click to view fullscreen</p>
              </div>
            </div>

            {/* Thumbnails column */}
            <div className="flex flex-row lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`relative shrink-0 lg:shrink rounded-xl overflow-hidden transition-all duration-300 group ${
                    i === activeIndex
                      ? 'ring-2 ring-[#d4853a] shadow-lg w-28 lg:w-auto lg:h-[calc(25%-0.75rem)]'
                      : 'ring-1 ring-transparent hover:ring-white/40 w-24 lg:w-auto lg:h-[calc(25%-0.75rem)] opacity-70 hover:opacity-100'
                  }`}
                  style={{ aspectRatio: '16/10' }}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${i + 1}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {i === activeIndex && (
                    <div className="absolute inset-0 bg-[#d4853a]/10" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-[#0a0a0a]/98 backdrop-blur-sm flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
            aria-label="Close gallery"
          >
            <i className="ri-close-line text-xl" />
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-5 z-10 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium">
            {lightboxIndex + 1} / {images.length}
          </div>

          {/* Main image */}
          <div
            className="relative max-w-[90vw] max-h-[80vh]"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <img
              src={images[lightboxIndex]}
              alt={`${projectTitle} - Full View ${lightboxIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
          </div>

          {/* Navigation arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-all duration-300"
            aria-label="Next image"
          >
            <i className="ri-arrow-right-line text-xl" />
          </button>

          {/* Thumbnail strip at bottom */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 max-w-[80vw] overflow-x-auto px-2">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(i); }}
                className={`shrink-0 w-14 h-10 md:w-16 md:h-11 rounded-lg overflow-hidden transition-all duration-300 ${
                  i === lightboxIndex ? 'ring-2 ring-[#d4853a] opacity-100' : 'opacity-50 hover:opacity-80'
                }`}
              >
                <img src={img} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}